import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Map, { Marker, Popup, NavigationControl } from "react-map-gl/mapbox";
import {
  Heart,
  Bed,
  Bath,
  Square,
  MapPin,
  SlidersHorizontal,
  LocateFixed,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  PROPERTY_TYPES,
  OPERATION_TYPES,
  ESTADO_OPTIONS,
  formatOperationTypes,
  CURRENCIES,
  formatPropertyPrice,
  formatPricePerM2,
  getPropertyPrice,
  isPriceOnRequest,
} from "../utils/formatters";
import { api } from "../services/apiClient";
import { getMapboxToken } from "../utils/mapbox";

const MAPBOX_TOKEN = getMapboxToken();

const CATEGORIES = [
  { value: "Residencial", color: "hsl(145, 50%, 45%)" },
  { value: "Comercial", color: "hsl(210, 60%, 50%)" },
  { value: "Industrial", color: "hsl(25, 55%, 50%)" },
];

const Stepper = ({ label, value, onChange }) => {
  const num = parseInt(value) || 0;
  return (
    <div>
      <label className="text-xs font-semibold text-muted-foreground mb-1 block">
        {label}
      </label>
      <div className="flex items-center h-10 border border-input rounded-md bg-background overflow-hidden">
        <button
          type="button"
          onClick={() => onChange(num <= 1 ? "" : String(num - 1))}
          disabled={num === 0}
          className="px-3 h-full text-base leading-none border-r border-input hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          −
        </button>
        <span className="flex-1 text-center text-sm font-medium">
          {num === 0 ? (
            <span className="text-muted-foreground text-xs">Cualquiera</span>
          ) : (
            num
          )}
        </span>
        <button
          type="button"
          onClick={() => onChange(String(num + 1))}
          disabled={num >= 10}
          className="px-3 h-full text-base leading-none border-l border-input hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          +
        </button>
      </div>
    </div>
  );
};

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState("pending"); // pending | granted | denied
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const geoRequested = useRef(false);
  const debounceRef = useRef(null);
  const ITEMS_PER_PAGE = 24;
  const [geoFallback, setGeoFallback] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [inputValues, setInputValues] = useState({
    minPrice: "",
    maxPrice: "",
    minLandSize: "",
    maxLandSize: "",
    minConstructionSize: "",
    maxConstructionSize: "",
  });
  const [filters, setFilters] = useState({
    category: searchParams.get("category") || "all",
    property_type: "all",
    estado: "all",
    status: searchParams.get("status") || "all",
    currency: "MXN",
    includePriceOnRequest: true,
    minPrice: "",
    maxPrice: "",
    minLandSize: "",
    maxLandSize: "",
    minConstructionSize: "",
    maxConstructionSize: "",
    bedrooms: "",
    bathrooms: "",
    parking: "",
  });
  const [viewState, setViewState] = useState({
    longitude: -99.1332,
    latitude: 19.4326,
    zoom: 11,
  });

  // Request geolocation on mount
  useEffect(() => {
    if (geoRequested.current) return;
    geoRequested.current = true;

    if (!navigator.geolocation) {
      setLocationStatus("denied");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const loc = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setUserLocation(loc);
        setLocationStatus("granted");
        setViewState((prev) => ({
          ...prev,
          longitude: loc.lng,
          latitude: loc.lat,
          zoom: 12,
        }));
      },
      () => {
        setLocationStatus("denied");
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 },
    );
  }, []);

  // Fetch when filters, page, userLocation, or geoFallback changes
  useEffect(() => {
    fetchProperties();
  }, [filters, currentPage, userLocation, geoFallback]);

  // Sync URL params to filters
  useEffect(() => {
    const categoryFromURL = searchParams.get("category");
    const propertyTypeFromURL = searchParams.get("property_type");
    const estadoFromURL = searchParams.get("estado");
    const statusFromURL = searchParams.get("status");

    if (
      categoryFromURL ||
      propertyTypeFromURL ||
      estadoFromURL ||
      statusFromURL
    ) {
      setGeoFallback(false);
      setFilters((prev) => ({
        ...prev,
        category: categoryFromURL || "all",
        property_type: propertyTypeFromURL || "all",
        estado: estadoFromURL || "all",
        status: statusFromURL || "all",
      }));
      setCurrentPage(1);
    }
  }, [searchParams]);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();

      if (filters.category !== "all")
        params.append("category", filters.category);
      if (filters.property_type !== "all")
        params.append("property_type", filters.property_type);
      if (filters.estado !== "all") params.append("estado", filters.estado);
      if (filters.status !== "all") params.append("status", filters.status);
      if (filters.currency) params.append("currency", filters.currency);
      params.append(
        "include_price_on_request",
        String(filters.includePriceOnRequest),
      );
      if (filters.minPrice) params.append("min_price", filters.minPrice);
      if (filters.maxPrice) params.append("max_price", filters.maxPrice);
      if (filters.minLandSize)
        params.append("min_land_size", filters.minLandSize);
      if (filters.maxLandSize)
        params.append("max_land_size", filters.maxLandSize);
      if (filters.minConstructionSize)
        params.append("min_construction_size", filters.minConstructionSize);
      if (filters.maxConstructionSize)
        params.append("max_construction_size", filters.maxConstructionSize);
      if (filters.bedrooms) params.append("bedrooms", filters.bedrooms);
      if (filters.bathrooms) params.append("bathrooms", filters.bathrooms);
      if (filters.parking) params.append("parking", filters.parking);

      // Geolocation — skip if in fallback mode (no nearby results found)
      if (userLocation && !geoFallback) {
        params.append("latitude", userLocation.lat);
        params.append("longitude", userLocation.lng);
        params.append("radius_km", "30");
      }

      // Pagination
      params.append("page", currentPage);
      params.append("limit", ITEMS_PER_PAGE);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await api.get(
        `/properties/search?${params.toString()}`,
        { auth: false, signal: controller.signal },
      );

      clearTimeout(timeoutId);

      if (!response.ok) throw new Error(`Error ${response.status}`);

      const data = await response.json();

      // Handle both paginated and legacy response
      const props = data.properties || (Array.isArray(data) ? data : []);

      // No nearby results — automatically fall back to showing all properties
      if (userLocation && !geoFallback && props.length === 0) {
        setGeoFallback(true);
        return;
      }

      setProperties(props);
      setTotalResults(data.total || props.length);
      setTotalPages(data.total_pages || 1);

      // Center map on result set when no user location is active or near-me fell back to general results.
      if (
        (geoFallback || !userLocation) &&
        props.length > 0 &&
        props[0].geometry
      ) {
        setViewState((prev) => ({
          ...prev,
          longitude: props[0].geometry.coordinates[0],
          latitude: props[0].geometry.coordinates[1],
          zoom: 11,
        }));
      }
    } catch (error) {
      if (error.name === "AbortError") {
        toast.error("La búsqueda tardó demasiado. Por favor intenta de nuevo.");
      } else {
        console.error("Error fetching properties:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const requestLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Tu navegador no soporta geolocalización");
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const loc = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setUserLocation(loc);
        setLocationStatus("granted");
        setGeoFallback(false);
        setViewState((prev) => ({
          ...prev,
          longitude: loc.lng,
          latitude: loc.lat,
          zoom: 12,
        }));
        setCurrentPage(1);
        setGeoLoading(false);
      },
      () => {
        toast.error(
          "No se pudo obtener tu ubicación. Verifica los permisos del navegador.",
        );
        setGeoLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const clearLocation = () => {
    setUserLocation(null);
    setLocationStatus("denied");
    setGeoFallback(false);
    setCurrentPage(1);
  };

  const handleFilterChange = (key, value) => {
    setGeoFallback(false);
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      ...(key === "category" ? { property_type: "all" } : {}),
    }));
    setCurrentPage(1);
  };

  const handleDebouncedChange = (key, value) => {
    setInputValues((prev) => ({ ...prev, [key]: value }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      handleFilterChange(key, value);
    }, 500);
  };

  const clearAllFilters = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const blank = {
      minPrice: "",
      maxPrice: "",
      minLandSize: "",
      maxLandSize: "",
      minConstructionSize: "",
      maxConstructionSize: "",
    };
    setFilters({
      category: "all",
      property_type: "all",
      estado: "all",
      status: "all",
      currency: "MXN",
      includePriceOnRequest: true,
      ...blank,
      bedrooms: "",
      bathrooms: "",
      parking: "",
    });
    setInputValues(blank);
    setGeoFallback(false);
    setCurrentPage(1);
  };

  const removeChip = (key) => {
    const textKeys = [
      "minPrice",
      "maxPrice",
      "minLandSize",
      "maxLandSize",
      "minConstructionSize",
      "maxConstructionSize",
    ];
    if (textKeys.includes(key)) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setInputValues((prev) => ({ ...prev, [key]: "" }));
    }
    if (key === "includePriceOnRequest") {
      handleFilterChange(key, true);
      return;
    }
    handleFilterChange(key, "");
  };

  const activeFilterCount = [
    filters.category !== "all",
    filters.property_type !== "all",
    filters.estado !== "all",
    filters.status !== "all",
    !!filters.minPrice,
    !!filters.maxPrice,
    !!filters.minLandSize,
    !!filters.maxLandSize,
    !!filters.minConstructionSize,
    !!filters.maxConstructionSize,
    !!filters.bedrooms,
    !!filters.bathrooms,
    !!filters.parking,
    filters.includePriceOnRequest === false,
  ].filter(Boolean).length;

  const advancedActiveCount = [
    !!filters.minPrice,
    !!filters.maxPrice,
    !!filters.minLandSize,
    !!filters.maxLandSize,
    !!filters.minConstructionSize,
    !!filters.maxConstructionSize,
    !!filters.bedrooms,
    !!filters.bathrooms,
    !!filters.parking,
    filters.includePriceOnRequest === false,
  ].filter(Boolean).length;

  const estadoLabel = ESTADO_OPTIONS.find(
    (e) => e.value === filters.estado,
  )?.label;
  const activeChips = [
    filters.property_type !== "all" && {
      key: "property_type",
      label: filters.property_type,
    },
    filters.estado !== "all" && {
      key: "estado",
      label: estadoLabel || filters.estado,
    },
    filters.minPrice && {
      key: "minPrice",
      label: `Desde $${Number(filters.minPrice).toLocaleString()} ${filters.currency}`,
    },
    filters.maxPrice && {
      key: "maxPrice",
      label: `Hasta $${Number(filters.maxPrice).toLocaleString()} ${filters.currency}`,
    },
    filters.includePriceOnRequest === false && {
      key: "includePriceOnRequest",
      label: "Solo precio publicado",
    },
    filters.minLandSize && {
      key: "minLandSize",
      label: `Terreno ≥ ${filters.minLandSize}m²`,
    },
    filters.maxLandSize && {
      key: "maxLandSize",
      label: `Terreno ≤ ${filters.maxLandSize}m²`,
    },
    filters.minConstructionSize && {
      key: "minConstructionSize",
      label: `Const. ≥ ${filters.minConstructionSize}m²`,
    },
    filters.maxConstructionSize && {
      key: "maxConstructionSize",
      label: `Const. ≤ ${filters.maxConstructionSize}m²`,
    },
    filters.bedrooms && { key: "bedrooms", label: `${filters.bedrooms} Rec.` },
    filters.bathrooms && {
      key: "bathrooms",
      label: `${filters.bathrooms} Baños`,
    },
    filters.parking && { key: "parking", label: `${filters.parking} Est.` },
  ].filter(Boolean);

  const getCategoryColor = (category) => {
    return CATEGORIES.find((c) => c.value === category)?.color || "#6b7280";
  };

  return (
    <div className="min-h-screen overflow-x-hidden">
      <Header />

      {/* Mapa principal arriba */}
      <div
        className="w-full h-[35vh] md:h-[45vh] lg:h-[50vh] relative"
        data-testid="map-container"
      >
        {MAPBOX_TOKEN ? (
          <Map
            {...viewState}
            onMove={(evt) => setViewState(evt.viewState)}
            style={{ width: "100%", height: "100%" }}
            mapStyle="mapbox://styles/mapbox/light-v11"
            mapboxAccessToken={MAPBOX_TOKEN}
            data-testid="map-view"
          >
            <NavigationControl position="top-left" />

            {properties.map(
              (property) =>
                property.geometry && (
                  <Marker
                    key={property.id || property._id}
                    longitude={property.geometry.coordinates[0]}
                    latitude={property.geometry.coordinates[1]}
                    onClick={(e) => {
                      e.originalEvent.stopPropagation();
                      setSelectedProperty(property);
                    }}
                  >
                    <div
                      style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "50%",
                        backgroundColor: getCategoryColor(property.category),
                        border: "2px solid white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        fontSize: "12px",
                        color: "white",
                        fontWeight: "bold",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                      }}
                      data-testid={`map-marker-${property.id || property._id}`}
                    >
                      {property.category.charAt(0)}
                    </div>
                  </Marker>
                ),
            )}

            {selectedProperty && selectedProperty.geometry && (
              <Popup
                longitude={selectedProperty.geometry.coordinates[0]}
                latitude={selectedProperty.geometry.coordinates[1]}
                onClose={() => setSelectedProperty(null)}
                closeButton={true}
                anchor="top"
              >
                <div className="p-3 max-w-xs" data-testid="property-popup">
                  <h3 className="font-bold text-sm md:text-base line-clamp-2">
                    {selectedProperty.title}
                  </h3>
                  <p className="text-xs md:text-sm text-gray-600 mb-2">
                    {formatPropertyPrice(selectedProperty)}
                  </p>
                  <Button
                    size="sm"
                    onClick={() =>
                      navigate(
                        `/property/${selectedProperty.id || selectedProperty._id}`,
                      )
                    }
                    className="w-full"
                  >
                    Ver Detalles
                  </Button>
                </div>
              </Popup>
            )}
          </Map>
        ) : (
          <div className="w-full h-full border border-border bg-muted/30 flex items-center justify-center text-sm text-muted-foreground px-4 text-center">
            El mapa no está disponible temporalmente. Configura
            REACT_APP_MAPBOX_TOKEN en el entorno de despliegue.
          </div>
        )}

        {MAPBOX_TOKEN && !loading && properties.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-4">
            <div className="max-w-md rounded-lg border border-border bg-background/95 backdrop-blur-sm shadow-lg px-4 py-3 text-center pointer-events-auto">
              <p className="text-sm font-semibold text-foreground mb-1">
                No hay propiedades para mostrar en el mapa
              </p>
              <p className="text-xs text-muted-foreground">
                Esto no significa que Mapbox esté fallando. Normalmente indica
                que no hay resultados con los filtros actuales o que aún no hay
                inventario publicado en esta búsqueda.
              </p>
            </div>
          </div>
        )}
      </div>

      <div
        className="sticky top-16 md:top-20 z-40 backdrop-blur-xl bg-background/80 border-b border-border/40"
        data-testid="filter-bar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 md:py-4">
          {/* Row 1: count + geo + mobile toggle */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-bold">{totalResults} Propiedades</h2>
              {userLocation ? (
                <button
                  onClick={clearLocation}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
                  data-testid="location-active-badge"
                >
                  <LocateFixed className="w-3 h-3" />
                  Usando mi ubicación
                  <span className="ml-1 opacity-60">✕</span>
                </button>
              ) : (
                <button
                  onClick={requestLocation}
                  disabled={geoLoading}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="request-location-btn"
                >
                  {geoLoading ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <LocateFixed className="w-3 h-3" />
                  )}
                  {geoLoading ? "Buscando ubicación..." : "Usar mi ubicación"}
                </button>
              )}
              {userLocation && geoFallback && (
                <span className="text-xs text-muted-foreground">
                  Sin resultados cercanos, mostrando resultados generales.
                </span>
              )}
              {activeFilterCount > 0 && (
                <button
                  onClick={clearAllFilters}
                  className="text-xs text-muted-foreground hover:text-destructive underline-offset-2 hover:underline transition-colors"
                  data-testid="clear-all-filters"
                >
                  Limpiar filtros
                </button>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="md:hidden flex items-center"
              data-testid="toggle-filters"
            >
              <SlidersHorizontal className="w-4 h-4 mr-2" />
              Filtros
              {activeFilterCount > 0 && (
                <span className="ml-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </div>

          {/* Category pills — always visible */}
          <div className="flex items-center gap-2 flex-wrap mb-2">
            {[
              { value: "all", label: "Todas" },
              { value: "Residencial", label: "Residencial" },
              { value: "Comercial", label: "Comercial" },
              { value: "Industrial", label: "Industrial" },
            ].map((cat) => (
              <button
                key={cat.value}
                onClick={() => handleFilterChange("category", cat.value)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  filters.category === cat.value
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary"
                }`}
                data-testid={`cat-pill-${cat.value}`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Operation pills — always visible */}
          <div className="flex items-center gap-2 flex-wrap mb-3">
            {[{ value: "all", label: "Todas" }, ...OPERATION_TYPES].map(
              (op) => (
                <button
                  key={op.value}
                  onClick={() => handleFilterChange("status", op.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    filters.status === op.value
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary"
                  }`}
                  data-testid={`op-pill-${op.value}`}
                >
                  {op.label}
                </button>
              ),
            )}
          </div>

          {/* Secondary filters — collapsible on mobile, always on desktop */}
          <div
            className={`space-y-3 ${showFilters ? "block" : "hidden md:block"}`}
          >
            {/* Tipo de Propiedad + Estado + Moneda */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground mb-1 block">
                  Tipo de Propiedad
                </label>
                <Select
                  value={filters.property_type}
                  onValueChange={(value) =>
                    handleFilterChange("property_type", value)
                  }
                >
                  <SelectTrigger
                    className="h-10"
                    data-testid="filter-property-type"
                  >
                    <SelectValue placeholder="Todos los Tipos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los Tipos</SelectItem>
                    {filters.category !== "all" &&
                      PROPERTY_TYPES[filters.category]?.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    {filters.category === "all" &&
                      [...new Set(Object.values(PROPERTY_TYPES).flat())].map(
                        (type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ),
                      )}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-semibold text-muted-foreground mb-1 block">
                  Estado de la Propiedad
                </label>
                <Select
                  value={filters.estado}
                  onValueChange={(value) => handleFilterChange("estado", value)}
                >
                  <SelectTrigger className="h-10" data-testid="filter-estado">
                    <SelectValue placeholder="Nuevo o Segunda Mano" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Nuevo o Segunda Mano</SelectItem>
                    {ESTADO_OPTIONS.map((e) => (
                      <SelectItem key={e.value} value={e.value}>
                        {e.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs font-semibold text-muted-foreground mb-1 block">
                  Moneda
                </label>
                <div className="flex gap-2 h-10">
                  {CURRENCIES.map((c) => c.value).map((cur) => (
                    <button
                      key={cur}
                      onClick={() => handleFilterChange("currency", cur)}
                      className={`flex-1 rounded-md text-xs font-semibold border transition-colors ${
                        filters.currency === cur
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background text-muted-foreground border-input hover:border-primary/50 hover:text-foreground"
                      }`}
                      data-testid={`currency-${cur}`}
                    >
                      {cur}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Más filtros toggle */}
            <button
              onClick={() => setShowAdvancedFilters((p) => !p)}
              className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
              data-testid="toggle-advanced-filters"
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              {showAdvancedFilters ? "Menos filtros" : "Más filtros"}
              {advancedActiveCount > 0 && (
                <span className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                  {advancedActiveCount}
                </span>
              )}
            </button>

            {/* Advanced filters */}
            {showAdvancedFilters && (
              <div className="space-y-3 pt-1">
                {/* Price */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground mb-1 block">
                      Precio Mínimo ({filters.currency})
                    </label>
                    <Input
                      type="number"
                      placeholder="Precio Mín"
                      value={inputValues.minPrice}
                      onChange={(e) =>
                        handleDebouncedChange("minPrice", e.target.value)
                      }
                      className="h-10"
                      data-testid="filter-min-price"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground mb-1 block">
                      Precio Máximo ({filters.currency})
                    </label>
                    <Input
                      type="number"
                      placeholder="Precio Máx"
                      value={inputValues.maxPrice}
                      onChange={(e) =>
                        handleDebouncedChange("maxPrice", e.target.value)
                      }
                      className="h-10"
                      data-testid="filter-max-price"
                    />
                  </div>
                </div>

                <div className="rounded-md border border-border/70 bg-muted/30 px-3 py-2">
                  <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.includePriceOnRequest}
                      onChange={(e) =>
                        handleFilterChange(
                          "includePriceOnRequest",
                          e.target.checked,
                        )
                      }
                      className="h-4 w-4"
                      data-testid="filter-include-price-on-request"
                    />
                    Incluir propiedades con "Precio a consultar"
                  </label>
                </div>

                {/* Land + Construction sizes */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground mb-1 block">
                      Terreno Mín (m²)
                    </label>
                    <Input
                      type="number"
                      placeholder="Mín"
                      value={inputValues.minLandSize}
                      onChange={(e) =>
                        handleDebouncedChange("minLandSize", e.target.value)
                      }
                      className="h-10"
                      data-testid="filter-min-land"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground mb-1 block">
                      Terreno Máx (m²)
                    </label>
                    <Input
                      type="number"
                      placeholder="Máx"
                      value={inputValues.maxLandSize}
                      onChange={(e) =>
                        handleDebouncedChange("maxLandSize", e.target.value)
                      }
                      className="h-10"
                      data-testid="filter-max-land"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground mb-1 block">
                      Construcción Mín (m²)
                    </label>
                    <Input
                      type="number"
                      placeholder="Mín"
                      value={inputValues.minConstructionSize}
                      onChange={(e) =>
                        handleDebouncedChange(
                          "minConstructionSize",
                          e.target.value,
                        )
                      }
                      className="h-10"
                      data-testid="filter-min-construction"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-muted-foreground mb-1 block">
                      Construcción Máx (m²)
                    </label>
                    <Input
                      type="number"
                      placeholder="Máx"
                      value={inputValues.maxConstructionSize}
                      onChange={(e) =>
                        handleDebouncedChange(
                          "maxConstructionSize",
                          e.target.value,
                        )
                      }
                      className="h-10"
                      data-testid="filter-max-construction"
                    />
                  </div>
                </div>

                {/* Steppers */}
                <div className="grid grid-cols-3 gap-3">
                  <Stepper
                    label="Recámaras"
                    value={filters.bedrooms}
                    onChange={(v) => handleFilterChange("bedrooms", v)}
                  />
                  <Stepper
                    label="Baños"
                    value={filters.bathrooms}
                    onChange={(v) => handleFilterChange("bathrooms", v)}
                  />
                  <Stepper
                    label="Estacionamientos"
                    value={filters.parking}
                    onChange={(v) => handleFilterChange("parking", v)}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Active filter chips */}
          {activeChips.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-border/40">
              {activeChips.map((chip) => (
                <span
                  key={chip.key}
                  className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20"
                >
                  {chip.label}
                  <button
                    onClick={() => removeChip(chip.key)}
                    className="ml-0.5 w-4 h-4 flex items-center justify-center rounded-full hover:bg-primary/20 transition-colors text-[10px]"
                    aria-label={`Quitar filtro ${chip.label}`}
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-8">
        <div className="mb-6">
          <h2
            className="text-xl md:text-2xl font-bold mb-2"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            {totalResults} Propiedades Encontradas
          </h2>
          <p className="text-sm md:text-base text-muted-foreground">
            {userLocation && !geoFallback
              ? "Resultados cerca de tu ubicación"
              : "Resultados de tu búsqueda"}
            {totalPages > 1 && ` · Página ${currentPage} de ${totalPages}`}
          </p>
        </div>

        {userLocation && geoFallback && !loading && properties.length > 0 && (
          <div className="mb-5 flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <LocateFixed className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              No encontramos propiedades cerca de tu ubicación.{" "}
              <strong>Mostrando todas las propiedades disponibles.</strong>
            </span>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : properties.length === 0 ? (
          <div className="text-center py-16">
            <MapPin className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
            <p className="text-base md:text-lg font-medium mb-1">
              No se encontraron propiedades
            </p>
            <p className="text-sm text-muted-foreground">
              Intenta ajustar los filtros de búsqueda
            </p>
            {userLocation && (
              <button
                onClick={clearLocation}
                className="mt-3 text-sm text-primary hover:underline"
              >
                Ver todas las propiedades
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {properties.map((property) => (
                <div
                  key={property.id || property._id}
                  onClick={() =>
                    navigate(`/property/${property.id || property._id}`)
                  }
                  className="bg-background border border-border/40 rounded-lg overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:border-primary/40 transition-all duration-300 cursor-pointer touch-manipulation relative"
                  data-testid={`property-card-${property.id || property._id}`}
                >
                  {property.is_featured && (
                    <div className="absolute top-2 right-2 z-10 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                      Destacada
                    </div>
                  )}
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={
                        property.image_urls?.[0] ||
                        "https://images.pexels.com/photos/35193584/pexels-photo-35193584.jpeg"
                      }
                      alt={property.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className="text-xs uppercase tracking-wider font-semibold"
                        style={{ color: getCategoryColor(property.category) }}
                      >
                        {property.category}
                        {property.property_type || property.subcategory
                          ? ` · ${property.property_type || property.subcategory}`
                          : ""}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {formatOperationTypes(
                          property.operation_types,
                          property.status,
                        )}
                        {property.estado
                          ? ` · ${property.estado === "nuevo" ? "Nuevo" : "Segunda Mano"}`
                          : ""}
                      </span>
                    </div>
                    <h3
                      className="text-base md:text-lg font-bold mb-1 line-clamp-2"
                      style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                      {property.title}
                    </h3>
                    <p className="text-xs text-muted-foreground mb-3 flex items-center line-clamp-1">
                      <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                      {property.address}
                    </p>

                    {property.bedrooms ||
                    property.bathrooms ||
                    property.land_size_m2 ? (
                      <div className="flex items-center space-x-3 mb-3 text-xs text-muted-foreground">
                        {property.bedrooms && (
                          <span className="flex items-center">
                            <Bed className="w-3 h-3 mr-1" />
                            {property.bedrooms}
                          </span>
                        )}
                        {property.bathrooms && (
                          <span className="flex items-center">
                            <Bath className="w-3 h-3 mr-1" />
                            {property.bathrooms}
                          </span>
                        )}
                        {property.land_size_m2 && (
                          <span className="flex items-center">
                            <Square className="w-3 h-3 mr-1" />
                            {property.land_size_m2} m²
                          </span>
                        )}
                      </div>
                    ) : null}

                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-lg md:text-xl font-bold mono">
                          {formatPropertyPrice(property)}
                        </p>
                        {property.construction_size_m2 &&
                          !isPriceOnRequest(property) &&
                          getPropertyPrice(property).price && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatPricePerM2(
                                getPropertyPrice(property).price,
                                property.construction_size_m2,
                                getPropertyPrice(property).currency,
                              )}
                            </p>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div
                className="flex items-center justify-center gap-2 mt-10 mb-4"
                data-testid="pagination-controls"
              >
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => {
                    setCurrentPage((p) => p - 1);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  data-testid="pagination-prev"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Anterior
                </Button>

                {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 7) {
                    pageNum = i + 1;
                  } else if (currentPage <= 4) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 3) {
                    pageNum = totalPages - 6 + i;
                  } else {
                    pageNum = currentPage - 3 + i;
                  }
                  return (
                    <Button
                      key={pageNum}
                      variant={pageNum === currentPage ? "default" : "outline"}
                      size="sm"
                      className="w-9 h-9 p-0"
                      onClick={() => {
                        setCurrentPage(pageNum);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      data-testid={`pagination-page-${pageNum}`}
                    >
                      {pageNum}
                    </Button>
                  );
                })}

                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => {
                    setCurrentPage((p) => p + 1);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  data-testid="pagination-next"
                >
                  Siguiente
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default SearchResults;
